import type { ReactNode } from "react";

import {
  CAMPAIGN_GRADIENT_PALETTE_SWATCHES,
  CAMPAIGN_GRADIENT_PRESETS,
  CAMPAIGN_IMAGE_OVERLAYS,
  COLOR_LAB_BUTTON_DEMOS,
  GRADIENT_REVIEW_ANGLES,
  MONOLITH_BOARD_STYLE,
  MONOLITH_SHELL_GRADIENT_CSS,
  MONOLITH_SHELL_STOPS,
  MONOLITH_UI_COLORS,
  STUDIO_ACCENT_SWATCHES,
  STUDIO_COLOR_HEX,
  buildCampaignGradientCss,
  getCampaignGradientPresetById,
} from "@/components/dashboard/monolithBoardStyle";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/classNames";

const RUSSET_PRESET = getCampaignGradientPresetById("russet-archive");
const VERDANT_PRESET = getCampaignGradientPresetById("verdant-gold");

function SectionTitle({ children, id }: { children: ReactNode; id: string }) {
  return (
    <h2
      id={id}
      className="scroll-mt-6 text-xl font-semibold uppercase tracking-[0.2em] [font-family:var(--color-lab-display),system-ui,sans-serif]"
    >
      {children}
    </h2>
  );
}

function HexSwatch({ hex, label }: { hex: string; label: string }) {
  return (
    <div className="flex flex-col gap-2">
      <div
        className="h-16 w-full border-[3px] border-neutral-950 shadow-[6px_6px_0_rgba(19,19,19,0.12)]"
        style={{ backgroundColor: hex }}
        title={hex}
      />
      <div>
        <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-neutral-600">{label}</p>
        <p className="mt-1 font-mono text-sm text-neutral-950">{hex}</p>
      </div>
    </div>
  );
}

export default function ColorsPage() {
  return (
    <div className="mx-auto min-h-full max-w-5xl px-5 py-12 pb-24 text-neutral-950 sm:px-8">
      <header className="border-b-[3px] border-neutral-950 pb-10">
        <p className="text-[11px] uppercase tracking-[0.28em] text-neutral-600">Design system · Colors</p>
        <h1 className="mt-4 text-4xl font-bold leading-tight tracking-tight [font-family:var(--color-lab-display),system-ui,sans-serif] sm:text-5xl">
          Color lab
        </h1>
        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-neutral-800">
          Every value here is either the source of truth in code or derived from it. Use this route to agree on a
          full scheme before threading new tokens through the app.
        </p>
        <nav className="mt-8 flex flex-wrap gap-3 text-sm">
          <a className="underline decoration-2 underline-offset-4 hover:text-neutral-700" href="#shell">
            Shell
          </a>
          <a className="underline decoration-2 underline-offset-4 hover:text-neutral-700" href="#ui-flat">
            UI colors
          </a>
          <a className="underline decoration-2 underline-offset-4 hover:text-neutral-700" href="#scheme">
            Scheme
          </a>
          <a className="underline decoration-2 underline-offset-4 hover:text-neutral-700" href="#buttons">
            Buttons
          </a>
          <a className="underline decoration-2 underline-offset-4 hover:text-neutral-700" href="#card-examples">
            Card examples
          </a>
          <a className="underline decoration-2 underline-offset-4 hover:text-neutral-700" href="#hero-palette">
            Hero palette
          </a>
          <a className="underline decoration-2 underline-offset-4 hover:text-neutral-700" href="#campaign">
            Campaign heroes
          </a>
          <a className="underline decoration-2 underline-offset-4 hover:text-neutral-700" href="#overlays">
            Overlays
          </a>
          <a className="underline decoration-2 underline-offset-4 hover:text-neutral-700" href="#angles">
            Angle study
          </a>
        </nav>
      </header>

      <div className="mt-14 space-y-20">
        <section className="space-y-6" aria-labelledby="shell">
          <SectionTitle id="shell">Monolith shell wash</SectionTitle>
          <p className="max-w-2xl text-neutral-800">
            Full-page vertical gradient behind the dashboard. Stops are listed left to right below.
          </p>
          <div
            className="h-28 border-[3px] border-neutral-950 shadow-[10px_10px_0_rgba(19,19,19,0.15)] sm:h-36"
            style={{ backgroundImage: MONOLITH_SHELL_GRADIENT_CSS }}
          />
          <pre className="overflow-x-auto border-2 border-neutral-950 bg-[#fffaf1] p-4 font-mono text-xs leading-relaxed text-neutral-900">
            {MONOLITH_SHELL_GRADIENT_CSS}
          </pre>
          <div className="grid gap-6 sm:grid-cols-3">
            {MONOLITH_SHELL_STOPS.map((stop) => (
              <HexSwatch key={stop.hex} hex={stop.hex} label={`${stop.label} (${stop.pct}%)`} />
            ))}
          </div>
        </section>

        <section className="space-y-6" aria-labelledby="ui-flat">
          <SectionTitle id="ui-flat">Flat UI tokens</SectionTitle>
          <p className="max-w-2xl text-neutral-800">
            Paper surfaces, button fills, and the ink tone (aligned with Tailwind{" "}
            <code className="rounded bg-neutral-200/80 px-1.5 py-0.5 font-mono text-[0.85em]">neutral-950</code>
            ).
          </p>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {MONOLITH_UI_COLORS.map((entry) => (
              <HexSwatch key={entry.id} hex={entry.hex} label={entry.label} />
            ))}
          </div>
        </section>

        <section className="space-y-6" aria-labelledby="scheme">
          <SectionTitle id="scheme">Application color scheme</SectionTitle>
          <div className="max-w-2xl space-y-4 text-neutral-800">
            <p>
              <strong className="text-neutral-950">Neutrals first:</strong> interfaces stay on warm paper (
              <code className="rounded bg-neutral-200/80 px-1.5 py-0.5 font-mono text-[0.85em]">
                {STUDIO_COLOR_HEX.paper}
              </code>
              ), black ink (
              <code className="rounded bg-neutral-200/80 px-1.5 py-0.5 font-mono text-[0.85em]">
                {STUDIO_COLOR_HEX.ink}
              </code>
              ), and warm off-white on solid buttons (
              <code className="rounded bg-neutral-200/80 px-1.5 py-0.5 font-mono text-[0.85em]">
                {STUDIO_COLOR_HEX.buttonTextOnInk}
              </code>
              ). Primary actions use <strong className="text-neutral-950">moss green</strong>; destructive actions
              use <strong className="text-neutral-950">terracotta</strong>; cancel and back-out controls stay
              neutral.
            </p>
            <p>
              <strong className="text-neutral-950">Accents from the posters:</strong>{" "}
              <span className="whitespace-nowrap font-mono text-sm text-neutral-950">
                {STUDIO_COLOR_HEX.accentPrimary}
              </span>{" "}
              (moss, Verdant gold) is the <strong className="text-neutral-950">affirmative action color</strong>.{" "}
              <span className="whitespace-nowrap font-mono text-sm text-neutral-950">
                {STUDIO_COLOR_HEX.accentSecondary}
              </span>{" "}
              (terracotta, Russet archive) is the <strong className="text-neutral-950">destructive color</strong>.
              Deep and gold stops from each family stay available for chips, hover tints, and map chrome.
            </p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {STUDIO_ACCENT_SWATCHES.map((swatch) => (
              <div key={swatch.id} className="flex flex-col gap-2">
                <HexSwatch hex={swatch.hex} label={swatch.label} />
                <p className="text-xs leading-relaxed text-neutral-700">{swatch.role}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-6" aria-labelledby="buttons">
          <SectionTitle id="buttons">Button system</SectionTitle>
          <p className="max-w-2xl text-neutral-800">
            Recipes composed from{" "}
            <code className="rounded bg-neutral-200/80 px-1.5 py-0.5 font-mono text-[0.85em]">
              MONOLITH_BOARD_STYLE
            </code>{" "}
            + frame utility. New app work should default to semantic roles: primary for affirmative actions,
            destructive for irreversible ones, cancel for backing out, and secondary for supporting neutral actions.
          </p>
          <div className="grid gap-8 border-[3px] border-neutral-950 bg-[#fffaf1] p-6 shadow-[10px_10px_0_rgba(19,19,19,0.12)] sm:p-8">
            {COLOR_LAB_BUTTON_DEMOS.map((demo) => (
              <div
                key={demo.id}
                className="border-b-2 border-neutral-950/15 pb-8 last:border-b-0 last:pb-0"
              >
                <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-neutral-500">
                  {demo.title}
                </p>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-neutral-800">{demo.description}</p>
                <div className="mt-5">
                  <Button variant={demo.variant} chrome={demo.chrome} type="button">
                    Sample
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-8" aria-labelledby="card-examples">
          <SectionTitle id="card-examples">Simple card + buttons</SectionTitle>
          <p className="max-w-2xl text-neutral-800">
            Same tokens as the dashboard:{" "}
            <code className="rounded bg-neutral-200/80 px-1.5 py-0.5 font-mono text-[0.85em]">
              MONOLITH_BOARD_STYLE.panelClassName
            </code>{" "}
            for the shell, then the semantic button recipes in the footer. Cards sit on the shell gradient like
            posters on the wall.
          </p>
          <div className="grid gap-10 lg:grid-cols-2">
            <article
              className={cn(
                "flex max-w-xl flex-col rounded-[28px] p-6 sm:p-7",
                MONOLITH_BOARD_STYLE.panelClassName,
              )}
            >
              <p className="text-[10px] uppercase tracking-[0.28em] text-neutral-600">Example A</p>
              <h3 className="mt-2 text-xl font-bold uppercase tracking-tight [font-family:var(--color-lab-display),system-ui,sans-serif]">
                Session notes
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-neutral-800">
                Short body copy on paper. Primary confirms the main outcome; cancel backs out without competing with
                the green CTA.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3 border-t-2 border-neutral-950/12 pt-6">
                <Button variant="primary" chrome="frame" type="button">
                  Save notes
                </Button>
                <Button variant="cancel" chrome="frame" type="button">
                  Cancel
                </Button>
              </div>
            </article>

            <article
              className={cn(
                "flex max-w-xl flex-col rounded-[28px] p-6 sm:p-7",
                MONOLITH_BOARD_STYLE.panelClassName,
              )}
            >
              <p className="text-[10px] uppercase tracking-[0.28em] text-neutral-600">Example B</p>
              <h3 className="mt-2 text-xl font-bold uppercase tracking-tight [font-family:var(--color-lab-display),system-ui,sans-serif]">
                Branching path
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-neutral-800">
                When the story splits, keep one green primary for the default continuation, then use secondary and
                muted for alternate branches that are not destructive.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3 border-t-2 border-neutral-950/12 pt-6">
                <Button variant="primary" chrome="frame" type="button">
                  Continue
                </Button>
                <Button variant="secondary" chrome="frame" type="button">
                  Forest path
                </Button>
                <Button variant="muted" chrome="frame" type="button">
                  Desert path
                </Button>
              </div>
            </article>
          </div>
        </section>

        <section className="space-y-8" aria-labelledby="campaign">
          <div>
            <SectionTitle id="campaign">Campaign image gradients</SectionTitle>
            <p className="mt-4 max-w-2xl text-neutral-800">
              Two hero families: a campaign ID hashes to{" "}
              <strong className="font-semibold text-neutral-950">Russet archive</strong> or{" "}
              <strong className="font-semibold text-neutral-950">Verdant gold</strong>. Below, every stop from
              both gradients is laid out as flat swatches so you can shortlist hexes for future tokens
              (accents, borders, badges, map chrome).
            </p>
          </div>

          <div className="space-y-4" aria-labelledby="hero-palette">
            <h3
              id="hero-palette"
              className="scroll-mt-6 text-lg font-semibold uppercase tracking-[0.18em] [font-family:var(--color-lab-display),system-ui,sans-serif]"
            >
              Hero palette — candidate hexes
            </h3>
            <p className="max-w-2xl text-sm leading-relaxed text-neutral-800">
              Six unique stops (three per gradient). Labels show which poster and gradient position they come
              from.
            </p>
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {CAMPAIGN_GRADIENT_PALETTE_SWATCHES.map((swatch) => (
                <HexSwatch
                  key={`${swatch.presetId}-${swatch.stopPct}`}
                  hex={swatch.hex}
                  label={`${swatch.presetLabel} · ${swatch.stopPct}%`}
                />
              ))}
            </div>
          </div>

          <div className="space-y-12">
            {CAMPAIGN_GRADIENT_PRESETS.map((preset) => {
              const css = buildCampaignGradientCss(preset.stops);
              return (
                <article
                  key={preset.id}
                  className="border-[3px] border-neutral-950 bg-[#fffaf1] shadow-[12px_12px_0_rgba(19,19,19,0.14)]"
                >
                  <div className="grid lg:grid-cols-[1.2fr_1fr]">
                    <div className="relative min-h-[200px] border-b-[3px] border-neutral-950 lg:min-h-[240px] lg:border-b-0 lg:border-r-[3px]">
                      <div className="absolute inset-0" style={{ backgroundImage: css }} />
                      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(0,0,0,0.14),rgba(0,0,0,0.54))]" />
                      <div className="relative flex h-full flex-col justify-end p-5">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-white/80">Sample overlay</p>
                        <p className="mt-2 text-2xl font-bold uppercase tracking-tight text-white [font-family:var(--color-lab-display),system-ui,sans-serif]">
                          {preset.label}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col justify-center gap-4 p-5 sm:p-6">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.26em] text-neutral-500">Preset id</p>
                        <p className="mt-1 font-mono text-sm">{preset.id}</p>
                      </div>
                      <p className="text-sm leading-relaxed text-neutral-800">{preset.note}</p>
                      <pre className="overflow-x-auto border-2 border-neutral-950/20 bg-white/60 p-3 font-mono text-[11px] leading-relaxed">
                        {css}
                      </pre>
                      <div className="flex flex-wrap gap-2">
                        {preset.stops.map((s) => (
                          <span
                            key={`${preset.id}-${s.pct}`}
                            className="inline-flex items-center gap-2 border-2 border-neutral-950 bg-white px-2 py-1 font-mono text-[11px]"
                          >
                            <span
                              className="h-4 w-4 border border-neutral-950"
                              style={{ backgroundColor: s.hex }}
                            />
                            {s.hex}
                            <span className="text-neutral-500">{s.pct}%</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="space-y-6" aria-labelledby="overlays">
          <SectionTitle id="overlays">Hero overlays</SectionTitle>
          <p className="max-w-2xl text-neutral-800">
            These sit on top of the campaign gradient (multiply / darken the poster). Shown here over{" "}
            <strong className="font-semibold">Russet archive</strong> for context.
          </p>
          <div className="grid gap-6 md:grid-cols-2">
            {CAMPAIGN_IMAGE_OVERLAYS.map((overlay) => (
              <div
                key={overlay.id}
                className="border-[3px] border-neutral-950 bg-[#fffaf1] shadow-[8px_8px_0_rgba(19,19,19,0.12)]"
              >
                <div className="relative h-40 border-b-[3px] border-neutral-950">
                  {RUSSET_PRESET ? (
                    <>
                      <div
                        className="absolute inset-0"
                        style={{
                          backgroundImage: buildCampaignGradientCss(RUSSET_PRESET.stops),
                        }}
                      />
                      <div className="absolute inset-0" style={{ backgroundImage: overlay.css }} />
                    </>
                  ) : null}
                </div>
                <div className="p-4">
                  <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-neutral-600">
                    {overlay.label}
                  </p>
                  <pre className="mt-2 overflow-x-auto font-mono text-[10px] leading-relaxed text-neutral-800">
                    {overlay.css}
                  </pre>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-10" aria-labelledby="angles">
          <SectionTitle id="angles">Gradient angle study</SectionTitle>
          <p className="max-w-2xl text-neutral-800">
            Same stops as each active preset, shown at different linear angles (for print / poster direction).
          </p>
          <div className="space-y-8">
            {[
              { key: "russet", label: "Russet archive", preset: RUSSET_PRESET },
              { key: "verdant", label: "Verdant gold", preset: VERDANT_PRESET },
            ].map((row) => {
              const preset = row.preset;
              if (!preset) {
                return null;
              }
              return (
                <div key={row.key} className="space-y-4">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-neutral-700 [font-family:var(--color-lab-display),system-ui,sans-serif]">
                    {row.label}
                  </h3>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                    {GRADIENT_REVIEW_ANGLES.map((angle) => {
                      const css = buildCampaignGradientCss(preset.stops, angle);
                      return (
                        <div
                          key={`${row.key}-${angle}`}
                          className="overflow-hidden border-[3px] border-neutral-950 bg-[#fffaf1] shadow-[6px_6px_0_rgba(19,19,19,0.12)]"
                        >
                          <div className="h-32" style={{ backgroundImage: css }} />
                          <p className="border-t-[3px] border-neutral-950 bg-white px-3 py-2 text-center font-mono text-xs">
                            {angle}°
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
